import {
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
  useMemo,
  useRef,
  useState
} from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import {
  calendarDays,
  dateToIso,
  type DateSegments,
  resolveCalendarOpen,
  segmentsFromIso,
  segmentsOnBlur,
  segmentsToIso,
  startOfMonth
} from "../lib/date-input";
import { useI18n } from "../hooks/use-i18n";

type SegmentKey = keyof DateSegments;

interface DateFieldProps {
  label: "From" | "To";
  segments: DateSegments;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSegments: (segments: DateSegments) => void;
}

const WEEKDAYS = {
  en: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  zh: ["日", "一", "二", "三", "四", "五", "六"]
};

export function DateField({
  label,
  segments,
  open,
  onOpen,
  onClose,
  onSegments
}: DateFieldProps) {
  const { language, locale, t } = useI18n();
  const today = useMemo(() => new Date(), []);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(today));
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const selectedIso = segmentsToIso(segments);
  const days = useMemo(() => calendarDays(viewMonth), [viewMonth]);
  const monthFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric"
      }),
    [locale]
  );
  const dayFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "long",
        day: "numeric"
      }),
    [locale]
  );
  const displayLabel = label === "From" ? t("from") : t("to");
  const labelled = (suffix: string) =>
    language === "zh" ? `${displayLabel}${suffix}` : `${displayLabel} ${suffix}`;

  const openCalendar = () => {
    const resolution = resolveCalendarOpen(segments, viewMonth, today);
    setViewMonth(resolution.viewMonth);
    if (resolution.shouldCommit) onSegments(resolution.segments);
    onOpen();
  };

  const handleOuterClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("input, button")) return;
    openCalendar();
  };

  const changeSegment =
    (key: SegmentKey, nextKey?: "month" | "day") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const maxLength = key === "year" ? 4 : 2;
      const value = event.target.value.replace(/\D/g, "").slice(0, maxLength);
      onSegments({ ...segments, [key]: value });
      if (value.length === maxLength) {
        if (nextKey === "month") monthRef.current?.focus();
        if (nextKey === "day") dayRef.current?.focus();
      }
    };

  const normalizeDraft =
    (key: SegmentKey) => (event: FocusEvent<HTMLInputElement>) => {
      const next = segmentsOnBlur(key, event.currentTarget.value, segments);
      if (next) onSegments(next);
    };

  const selectDate = (date: Date) => {
    onSegments(segmentsFromIso(dateToIso(date)));
    onClose();
  };

  const moveMonth = (offset: number) => {
    setViewMonth(
      new Date(viewMonth.getFullYear(), viewMonth.getMonth() + offset, 1)
    );
  };

  return (
    <div
      className={`date-field date-field--${label.toLowerCase()}${
        open ? " date-field--open" : ""
      }`}
      role="group"
      aria-label={labelled(t("releaseDate"))}
      onClick={handleOuterClick}
    >
      <span className="date-field__label">{displayLabel}</span>
      <input
        className="date-field__segment date-field__segment--year"
        value={segments.year}
        placeholder="YYYY"
        inputMode="numeric"
        maxLength={4}
        aria-label={labelled(t("year"))}
        onChange={changeSegment("year", "month")}
        onBlur={normalizeDraft("year")}
      />
      <input
        ref={monthRef}
        className="date-field__segment"
        value={segments.month}
        placeholder="MM"
        inputMode="numeric"
        maxLength={2}
        aria-label={labelled(t("month"))}
        onChange={changeSegment("month", "day")}
        onBlur={normalizeDraft("month")}
      />
      <input
        ref={dayRef}
        className="date-field__segment"
        value={segments.day}
        placeholder="DD"
        inputMode="numeric"
        maxLength={2}
        aria-label={labelled(t("day"))}
        onChange={changeSegment("day")}
        onBlur={normalizeDraft("day")}
      />
      <button
        type="button"
        className="date-field__calendar-button"
        aria-label={`${t("openCalendar")}${
          language === "zh" ? "：" : ": "
        }${displayLabel}`}
        aria-expanded={open}
        onClick={openCalendar}
      >
        <CalendarDays size={13} aria-hidden="true" />
      </button>

      {open ? (
        <div
          className="calendar-popover"
          role="dialog"
          aria-label={labelled(t("releaseDate"))}
        >
          <header className="calendar-popover__header">
            <button
              type="button"
              aria-label={t("previousMonth")}
              onClick={() => moveMonth(-1)}
            >
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <strong>{monthFormatter.format(viewMonth)}</strong>
            <button
              type="button"
              aria-label={t("nextMonth")}
              onClick={() => moveMonth(1)}
            >
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </header>
          <div className="calendar-popover__weekdays" aria-hidden="true">
            {WEEKDAYS[language].map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>
          <div className="calendar-popover__days">
            {days.map((date) => {
              const iso = dateToIso(date);
              const outside = date.getMonth() !== viewMonth.getMonth();
              const selected = selectedIso === iso;
              const isToday = dateToIso(today) === iso;
              return (
                <button
                  type="button"
                  className={[
                    outside ? "calendar-day--outside" : "",
                    selected ? "calendar-day--selected" : "",
                    isToday ? "calendar-day--today" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={dayFormatter.format(date)}
                  aria-pressed={selected}
                  aria-current={isToday ? "date" : undefined}
                  key={iso}
                  onClick={() => selectDate(date)}
                >
                  <span>{date.getDate()}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
